/** @format */

import { ResourceReference, UserResourceReference } from "../../../types";
import { QueryProps } from "./ResourcesIndexUI";

/** Function to save query to local and session storage */
export const updateQueryStorage = (
  params: { [key: string]: string },
  subject?: string,
) => {
  const { sort, ..._params } = params;
  let localParams = new URLSearchParams(_params).toString();
  let sessionParams = new URLSearchParams(sort).toString();

  if (subject) {
    updateQueryLocalStorage(localParams, subject);
    updateQuerySessionStorage(sessionParams, subject);
  }
};

/** Set localStorage params / overwrite */
const updateQueryLocalStorage = (params: string, subject: string) => {
  const existingQuery = localStorage.getItem("PREV_QUERY") || "{}";
  const newQuery = JSON.parse(existingQuery);
  if (params) {
    newQuery[subject] = params;
  } else {
    delete newQuery[subject];
  }
  localStorage.setItem("PREV_QUERY", JSON.stringify(newQuery));
};

/** Set sessionStorage params / overwrite */
const updateQuerySessionStorage = (params: string, subject: string) => {
  const existingQuery = sessionStorage.getItem("PREV_QUERY") || "{}";
  const newQuery = JSON.parse(existingQuery);
  if (params) {
    newQuery[subject] = params;
  } else {
    delete newQuery[subject];
  }
  sessionStorage.setItem("PREV_QUERY", JSON.stringify(newQuery));
};

/** Function to turn selected filters into URLSearchParams compatible parameters */
export const getCurrentParams = (filters?: QueryProps) => {
  const { tags, types, sort, level, cats, qualification, specItem } =
    filters ?? {};
  const params: { [key: string]: string } = {};

  if (tags && tags.length > 0) params.tags = tags.join("|");
  if (types && types.length > 0) params.types = types.join(",");
  if (sort) params.sort = sort;
  if (level) params.level = level;
  if (qualification) params.qualification = qualification;
  if (cats && cats.length > 0) params.cats = cats.join(",");
  if (specItem) params.spec = specItem;

  return params;
};

/** Function to filter resources array based on queries set
 * @param { QueryProps } query - Object containing the set queries.
 * @param { (UserResourceReference | ResourceReference)[] } resources - Original array of resources to filter by.
 * @returns { (UserResourceReference | ResourceReference)[] } filtered resource array
 */
export const filterResources = (
  query: QueryProps,
  resources: (UserResourceReference | ResourceReference)[],
): (UserResourceReference | ResourceReference)[] => {
  let filtered = [...resources];

  if (query) {
    const { types, tags, level, specItem, cats, sort, qualification, status } =
      query;

    filtered = filtered.filter(
      (resource) =>
        (types.length === 0 || types.includes(resource.Type)) &&
        (tags.length === 0 ||
          Array.from(new Set(resource.Object.tags)).filter((rTag) =>
            tags.find(
              (tag) =>
                tag.toLocaleLowerCase().trim() ===
                rTag.toLocaleLowerCase().trim(),
            ),
          ).length === tags.length) &&
        (!level ||
          (Array.isArray(resource.Object.levels) &&
            resource.Object.levels.includes(level))) &&
        (!specItem ||
          (resource.SpecItem && resource.SpecItem.startsWith(specItem))) &&
        (!qualification || resource.Qualification === qualification) &&
        (cats.length === 0 ||
          Array.from(new Set(resource.Object?.categories)).filter((rCat) =>
            cats.includes(rCat),
          ).length > 0) &&
        (!status || resource.Status === status),
    );
    // Apply sort to filtered array if queried.
    // Can sort by name (asc, desc), date (asc, desc) and level (asc, desc).
    if (sort) {
      filtered.sort((a, b) => {
        switch (sort) {
          case "name":
          case "enw":
          case "name<":
          case "enw<":
            return (a.Object.title?.toLowerCase() ?? "") >
              (b.Object.title?.toLowerCase() ?? "")
              ? 1
              : -1;
          case "name>":
          case "enw>":
            return (a.Object.title?.toLowerCase() ?? "") >
              (b.Object.title?.toLowerCase() ?? "")
              ? -1
              : 1;
          case "date":
          case "date>":
            if (!!a.lastLog?.time && !!b.lastLog?.time)
              return new Date(a.lastLog?.time) > new Date(b.lastLog?.time)
                ? -1
                : 1;
            else return 0;
          case "date<":
            if (!!a.lastLog?.time && !!b.lastLog?.time)
              return new Date(a.lastLog?.time) > new Date(b.lastLog?.time)
                ? 1
                : -1;
            else return 0;
          case "level":
          case "level<":
            return (Array.isArray(a.Object.levels)
              ? [...a.Object.levels].sort()[0]
              : a.Object.levels
            )?.toLowerCase() >
              (Array.isArray(b.Object.levels)
                ? [...b.Object.levels].sort()[0]
                : b.Object.levels
              )?.toLowerCase()
              ? 1
              : -1;
          case "level>":
            return (Array.isArray(a.Object.levels)
              ? [...a.Object.levels].sort()[0]
              : a.Object.levels
            )?.toLowerCase() >
              (Array.isArray(b.Object.levels)
                ? [...b.Object.levels].sort()[0]
                : b.Object.levels
              )?.toLowerCase()
              ? -1
              : 1;
          default:
            return 0;
        }
      });
    }
  }

  return filtered;
};
